/**
 * Dev Deepawali rangoli — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_kartik`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const devDeepawaliRangoliPack: ToilePack = {
    slug: 'dev-deepawali-rangoli',
    category: 'faith',
    name: 'Dev Deepawali rangoli',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'diyas', label: 'Ring of diyas', subject: 'a round rangoli of concentric rings of diya flames around a central lotus, with eight petals pointing outward'},
        {key: 'ganga', label: 'Ganga waves', subject: 'a rangoli of flowing river waves crossing the centre with lamps floating on them, and ghat steps drawn in a border along one side'},
        {key: 'lotus', label: 'Lotus and lamp', subject: 'a symmetrical rangoli of a large open lotus holding a single lit diya, framed by a square border of paisley and dots'},
    ],
    styles: [
        {key: 'outline', label: 'Bold outline', style: 'bold even black outlines drawn as a coloring page, large open shapes easy to fill with colour', palette: 'black ink only', framing: 'centered with a wide white margin'},
        {key: 'dotgrid', label: 'Dot grid', style: 'a dot-grid rangoli guide, small black dots at the grid points with single continuous looping lines threaded around them', palette: 'black ink only', framing: 'the dot lattice filling the sheet edge to edge'},
        {key: 'template', label: 'Fine template', style: 'fine thin single-weight lines with dotted petal edges, a symmetrical copy guide for powder, flowers or chalk', palette: 'black ink only', framing: 'centered and perfectly symmetrical with a white margin'},
    ],
    directions: ['peacock', 'hexagon', 'bigger border'],
    directionHint: 'try: peacock',
    seo: {
        title: 'Dev Deepawali rangoli: diyas, Ganga and lotus',
        metaDescription: 'Nine black-and-white Dev Deepawali rangoli designs of diyas, the river and the lotus, in outline, dot-grid and template styles.',
        h1: 'Dev Deepawali rangoli: diyas, the Ganga and the lotus',
        tagline: 'Nine designs seen from above, three per motif, to colour in, join by dots or copy with a fine template.',
        intro: 'Rangoli for Dev Deepawali, the Kartik Purnima night when the ghats of Varanasi are lined with lamps for the Ganga. Each design is seen from above, built round a ring of diyas, the river with lamps on its waves, or a lotus holding a lit diya. Choose the bold outline to colour in, the dot grid to join, or the fine template to copy. Type a different motif, such as peacock, and the same treatments will carry it.',
        useCases: [
            'a doorstep rangoli for Kartik Purnima',
            'a colouring sheet for a festival night',
            'a practice sheet before drawing in powder',
            'a rangoli for a community or temple courtyard',
        ],
        faq: [
            {q: 'Are these printable?', a: 'Yes. Each is a black line drawing on pure white with no shading or grey tones, so it prints clean.'},
            {q: 'What is the dot grid for?', a: 'The dot grid shows small dots at each grid point with loops drawn between them, so you can join the dots and copy the design by hand.'},
            {q: 'Are the designs tied to Varanasi?', a: 'The Ganga and ghat design draws on that setting; the ring of diyas and the lotus suit any Kartik Purnima night.'},
            {q: 'Can I ask for a different motif?', a: 'Yes. A direction such as peacock is a request for the design, not a promised result.'},
        ],
    },
}
